/** Progress belongs to this device. Multiplayer scores remain server-owned. */
export interface PlayerProgress {
  xp: number;
  hits: number;
  victories: number;
  practiceRuns: number;
  bestStreak: number;
}

export interface ProgressReward {
  progress: PlayerProgress;
  gained: number;
  /** Badge IDs that crossed their target in this update. */
  unlocked: string[];
}

export interface ProgressBadge {
  id: string;
  name: string;
  emoji: string;
  description: string;
  unlocked: boolean;
  current: number;
  target: number;
}

const STORAGE_KEY = "pile-progress-v1";
const MAX_COUNT = Number.MAX_SAFE_INTEGER;

function count(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.min(MAX_COUNT, Math.max(0, Math.floor(value)))
    : 0;
}

function normalize(value: unknown): PlayerProgress {
  const source =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Partial<PlayerProgress>)
      : {};
  return {
    xp: count(source.xp),
    hits: count(source.hits),
    victories: count(source.victories),
    practiceRuns: count(source.practiceRuns),
    bestStreak: count(source.bestStreak),
  };
}

export function loadProgress(): PlayerProgress {
  try {
    return normalize(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null"));
  } catch {
    // Storage can be unavailable or contain data from an interrupted write.
    return normalize(null);
  }
}

export function saveProgress(progress: PlayerProgress): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalize(progress)));
  } catch {
    // In-memory progress continues when storage is blocked or full.
  }
}

/** Level 2 starts at 100 XP, level 3 at 250 XP, level 4 at 450 XP. */
export function levelInfo(xp: number): {
  level: number;
  current: number;
  needed: number;
  ratio: number;
} {
  const total = count(xp);
  // Completing n levels costs 100 + 150 + ... = 25n(n + 3).
  let completed = Math.floor((Math.sqrt(9 + (4 * total) / 25) - 3) / 2);
  const threshold = (levels: number) => 25 * levels * (levels + 3);
  // Keep exact boundaries correct even at very large floating-point inputs.
  while (completed > 0 && threshold(completed) > total) completed -= 1;
  while (threshold(completed + 1) <= total) completed += 1;
  const current = total - threshold(completed);
  const needed = 100 + 50 * completed;
  return { level: completed + 1, current, needed, ratio: current / needed };
}

export function badges(progress: PlayerProgress): ProgressBadge[] {
  const p = normalize(progress);
  const definitions = [
    {
      id: "first-hit",
      name: "Premier éclair",
      emoji: "⚡",
      description: "Trouver ton premier symbole commun.",
      value: p.hits,
      target: 1,
    },
    {
      id: "hits-25",
      name: "Œil affûté",
      emoji: "🎯",
      description: "Trouver 25 symboles communs.",
      value: p.hits,
      target: 25,
    },
    {
      id: "hits-100",
      name: "Cent symboles",
      emoji: "💯",
      description: "Trouver 100 symboles communs.",
      value: p.hits,
      target: 100,
    },
    {
      id: "streak-5",
      name: "Sans faute",
      emoji: "🔥",
      description: "Enchaîner 5 bonnes réponses sans erreur.",
      value: p.bestStreak,
      target: 5,
    },
    {
      id: "first-victory",
      name: "Première couronne",
      emoji: "👑",
      description: "Remporter une partie multijoueur.",
      value: p.victories,
      target: 1,
    },
    {
      id: "practice-3",
      name: "Échauffé à bloc",
      emoji: "🏋️",
      description: "Terminer 3 entraînements.",
      value: p.practiceRuns,
      target: 3,
    },
  ];
  return definitions.map(({ value, ...badge }) => ({
    ...badge,
    unlocked: value >= badge.target,
    current: Math.min(value, badge.target),
  }));
}

function reward(
  before: PlayerProgress,
  after: PlayerProgress,
  xp: number,
): ProgressReward {
  const previous = new Set(
    badges(before)
      .filter((badge) => badge.unlocked)
      .map((badge) => badge.id),
  );
  const total = Math.min(MAX_COUNT, after.xp + xp);
  const progress = { ...after, xp: total };
  return {
    progress,
    gained: total - after.xp,
    unlocked: badges(progress)
      .filter((badge) => badge.unlocked && !previous.has(badge.id))
      .map((badge) => badge.id),
  };
}

/** Call once for each correct hit confirmed by the game; each hit earns 10 XP. */
export function recordHit(
  progress: PlayerProgress,
  streak: number,
): ProgressReward {
  const before = normalize(progress);
  return reward(
    before,
    {
      ...before,
      hits: Math.min(MAX_COUNT, before.hits + 1),
      bestStreak: Math.max(before.bestStreak, 1, count(streak)),
    },
    10,
  );
}

/** Call once per completed multiplayer victory; shared wins also earn 100 XP. */
export function recordVictory(progress: PlayerProgress): ProgressReward {
  const before = normalize(progress);
  return reward(
    before,
    { ...before, victories: Math.min(MAX_COUNT, before.victories + 1) },
    100,
  );
}

/** Call once after all practice cards are solved; a finished run earns 50 XP. */
export function recordPracticeComplete(
  progress: PlayerProgress,
): ProgressReward {
  const before = normalize(progress);
  return reward(
    before,
    { ...before, practiceRuns: Math.min(MAX_COUNT, before.practiceRuns + 1) },
    50,
  );
}
